import { Button } from '@/components/ui/button';
import { cn } from '@/lib/tailwind-utils';
import {
  AssessmentFormat,
  CoachPortalInAppAssessment,
  CoachPortalSession,
  calculateReadingWPM,
  isProgressMonitorFormat,
  studentYearOption,
} from '@airreading/common/data';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Flag,
  Lightbulb,
  Minus,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  X,
} from 'lucide-react';
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import {
  AssessmentPhase,
  canMarkInAppAssessmentError,
  canSelectInAppAssessmentLastWord,
  formatCountdown,
  InAppAssessmentStats,
  InAppAssessmentStudent,
  isInAppAssessmentAttemptActive,
  tokenizePassage,
} from './in-app-assessment-utils';
import { AssessmentWindowProvider, useAssessmentWindow } from './in-app-assessment-window-context';
import { useInAppAssessmentRunner } from './use-in-app-assessment-runner';
import { useInAppAssessmentSubmit } from './use-in-app-assessment-submit';

type TimeUpClickMode = 'markErrors' | 'pickLastWord';

/**
 * Floating window the coach runs assessments from.
 *
 * Deliberately non-modal and undimmed: the coach is on a video call behind this and must stay able to
 * use the rest of the portal. Attempt state lives here in the opener document. When Picture-in-Picture
 * is active the shell is rendered into the PiP window with its own React root so clicks work, while this
 * component stays mounted so the timer and marks are not reset.
 */
export function InAppAssessmentWindow({
  session,
  open,
  onClose,
  pipWindow,
  pictureInPictureSupported,
  onEnterPictureInPicture,
}: {
  session: CoachPortalSession;
  open: boolean;
  onClose: () => void;
  pipWindow: Window | null;
  pictureInPictureSupported: boolean;
  onEnterPictureInPicture: () => void;
}) {
  const pipRootRef = useRef<Root | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [timeUpClickMode, setTimeUpClickMode] = useState<TimeUpClickMode>('markErrors');
  const isPictureInPicture = pipWindow != null && !pipWindow.closed;

  const groups = useMemo(() => timedContentGroups(session), [session]);
  const needsPicker = groups.length > 1;
  const [selectedKind, setSelectedKind] = useState<TimedContentKind | null>(
    needsPicker ? null : groups[0]?.kind ?? null,
  );
  const students = useMemo(() => (selectedKind ? studentsForKind(session, selectedKind) : []), [session, selectedKind]);
  const [selectedAssessmentResultId, setSelectedAssessmentResultId] = useState<string | null>(() =>
    firstAssessmentResultId(students),
  );

  const runner = useInAppAssessmentRunner({ selectedAssessmentResultId });
  const selectedStudent = studentForResultId(students, selectedAssessmentResultId);
  const selectedAssessment = selectedStudent?.assessments.find(
    (assessment) => assessment.assessmentResultId === selectedAssessmentResultId,
  );
  const submitAssessment = useInAppAssessmentSubmit({
    selectedAssessmentResultId,
    words: tokensForAssessment(selectedAssessment),
    selectedState: runner.selectedState,
    markSubmitting: runner.markSubmitting,
    markSubmitted: runner.markSubmitted,
    revertPhase: runner.revertPhase,
  });
  const navigationLocked = selectedKind != null && isInAppAssessmentAttemptActive(runner.selectedState.phase);
  const headerTitle = selectedAssessment
    ? `${selectedAssessment.assessmentDisplayName} ${selectedAssessment.assessmentContentName}`
    : 'Select assessment';
  const closeGuardStudentName = navigationLocked ? selectedStudent?.displayName.trim() || 'This student' : null;
  const attemptActive = closeGuardStudentName != null;

  const requestClose = () => {
    if (attemptActive) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const selectKind = (kind: TimedContentKind) => {
    runner.pause();
    const nextStudents = studentsForKind(session, kind);
    setSelectedKind(kind);
    setSelectedAssessmentResultId(firstAssessmentResultId(nextStudents));
  };

  const body = (
    <InAppAssessmentWindowBody
      groups={groups}
      needsPicker={needsPicker}
      selectedKind={selectedKind}
      students={students}
      selectedAssessmentResultId={selectedAssessmentResultId}
      runner={runner}
      navigationLocked={navigationLocked}
      timeUpClickMode={timeUpClickMode}
      onTimeUpClickModeChange={setTimeUpClickMode}
      onSubmitAssessment={submitAssessment}
      onSelectKind={selectKind}
      onSelectStudent={(studentId) => {
        if (navigationLocked) {
          return;
        }
        runner.pause();
        const entry = students.find((row) => row.student.id === studentId);
        setSelectedAssessmentResultId(entry?.assessments[0]?.assessmentResultId ?? null);
      }}
      onSelectPmTab={(assessmentResultId) => {
        if (navigationLocked || assessmentResultId === selectedAssessmentResultId) {
          return;
        }
        runner.pause();
        setSelectedAssessmentResultId(assessmentResultId);
      }}
      onChangeContent={() => {
        if (navigationLocked) {
          return;
        }
        runner.pause();
        setSelectedKind(null);
      }}
    />
  );

  const frame = (
    <InAppAssessmentWindowFrame
      headerTitle={headerTitle}
      minimized={minimized}
      isPictureInPicture={isPictureInPicture}
      pictureInPictureSupported={pictureInPictureSupported}
      confirmCloseOpen={confirmCloseOpen}
      closeGuardStudentName={closeGuardStudentName}
      useDialogTitle={!isPictureInPicture}
      onContentElement={setContentElement}
      onToggleMinimized={() => setMinimized((current) => !current)}
      onEnterPictureInPicture={onEnterPictureInPicture}
      onRequestClose={requestClose}
      onStay={() => setConfirmCloseOpen(false)}
      onCloseAnyway={() => {
        setConfirmCloseOpen(false);
        onClose();
      }}
    >
      {body}
    </InAppAssessmentWindowFrame>
  );

  useLayoutEffect(() => {
    if (!pipWindow) {
      return;
    }

    const root = createRoot(pipWindow.document.body);
    pipRootRef.current = root;

    return () => {
      try {
        root.unmount();
      } catch {
        // The PiP document may already be gone when the browser closes the window.
      }
      if (pipRootRef.current === root) {
        pipRootRef.current = null;
      }
    };
  }, [pipWindow]);

  useLayoutEffect(() => {
    if (!isPictureInPicture || !pipWindow || !pipRootRef.current) {
      return;
    }

    pipRootRef.current.render(
      <AssessmentWindowProvider
        ownerDocument={pipWindow.document}
        ownerWindow={pipWindow}
        portalContainer={pipWindow.document.body}
      >
        {frame}
      </AssessmentWindowProvider>,
    );
  });

  if (isPictureInPicture) {
    return null;
  }

  return (
    <DialogPrimitive.Root
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) {
          requestClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <AssessmentWindowProvider ownerDocument={document} ownerWindow={window} portalContainer={contentElement}>
          {frame}
        </AssessmentWindowProvider>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function InAppAssessmentWindowFrame({
  headerTitle,
  minimized,
  isPictureInPicture,
  pictureInPictureSupported,
  confirmCloseOpen,
  closeGuardStudentName,
  useDialogTitle,
  onContentElement,
  onToggleMinimized,
  onEnterPictureInPicture,
  onRequestClose,
  onStay,
  onCloseAnyway,
  children,
}: {
  headerTitle: string;
  minimized: boolean;
  isPictureInPicture: boolean;
  pictureInPictureSupported: boolean;
  confirmCloseOpen: boolean;
  closeGuardStudentName: string | null;
  useDialogTitle: boolean;
  onContentElement: (element: HTMLDivElement | null) => void;
  onToggleMinimized: () => void;
  onEnterPictureInPicture: () => void;
  onRequestClose: () => void;
  onStay: () => void;
  onCloseAnyway: () => void;
  children: ReactNode;
}) {
  const Title = useDialogTitle ? DialogPrimitive.Title : 'h2';
  const shell = (
    <>
      <div className="flex items-center gap-2 bg-air-primary px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <Title className="truncate text-sm font-semibold">{headerTitle}</Title>
          {useDialogTitle ? (
            <DialogPrimitive.Description className="sr-only">In-app assessment</DialogPrimitive.Description>
          ) : (
            <p className="sr-only">In-app assessment</p>
          )}
        </div>
        {pictureInPictureSupported && !isPictureInPicture && (
          <button
            type="button"
            onClick={onEnterPictureInPicture}
            aria-label="Open assessment in picture-in-picture"
            className="rounded p-1 hover:bg-white/20"
            data-testid="in-app-assessment-enter-picture-in-picture"
          >
            <PictureInPicture2 className="h-4 w-4" aria-hidden />
          </button>
        )}
        {!isPictureInPicture && (
          <>
            <button
              type="button"
              onClick={onToggleMinimized}
              aria-label={minimized ? 'Expand assessment' : 'Minimize assessment'}
              className="rounded p-1 hover:bg-white/20"
              data-testid="in-app-assessment-minimize"
            >
              <Minus className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Close assessment"
              className="rounded p-1 hover:bg-white/20"
              data-testid="in-app-assessment-close"
              onClick={onRequestClose}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
      </div>

      <div
        className={cn('min-h-0 flex-1 flex-col overflow-hidden', !isPictureInPicture && minimized ? 'hidden' : 'flex')}
      >
        {children}
      </div>

      {confirmCloseOpen && closeGuardStudentName && (
        <InAppAssessmentCloseConfirm
          studentName={closeGuardStudentName}
          onStay={onStay}
          onCloseAnyway={onCloseAnyway}
        />
      )}
    </>
  );

  if (isPictureInPicture) {
    return (
      <div
        ref={onContentElement}
        className="relative flex h-full w-full flex-col overflow-hidden bg-background"
        data-testid="in-app-assessment-window"
      >
        {shell}
      </div>
    );
  }

  return (
    <DialogPrimitive.Content
      ref={onContentElement}
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => {
        if (confirmCloseOpen) {
          event.preventDefault();
          onStay();
          return;
        }
        if (closeGuardStudentName) {
          event.preventDefault();
          onRequestClose();
        }
      }}
      className={cn(
        'relative fixed bottom-4 right-4 z-50 flex w-[min(30rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl',
        minimized ? 'h-auto' : 'h-[min(40rem,calc(100vh-2rem))]',
      )}
      data-testid="in-app-assessment-window"
    >
      {shell}
    </DialogPrimitive.Content>
  );
}

type TimedContentKind = 'gla' | 'progress_monitor' | 'fluency_check';

const TIMED_CONTENT_LABEL: Record<TimedContentKind, string> = {
  gla: 'GLA',
  progress_monitor: 'Progress monitor',
  fluency_check: 'Fluency check',
};

interface TimedContentGroup {
  kind: TimedContentKind;
  label: string;
  requiresWaitingRoom: boolean;
  students: InAppAssessmentStudent[];
}

function timedContentKindFromFormat(format: AssessmentFormat): TimedContentKind | null {
  if (format === AssessmentFormat.grade_level_fluency_v2) {
    return 'gla';
  }
  if (format === AssessmentFormat.fluency_check) {
    return 'fluency_check';
  }
  if (isProgressMonitorFormat(format)) {
    return 'progress_monitor';
  }
  return null;
}

function compareTimedContentKinds(a: TimedContentKind, b: TimedContentKind): number {
  if (a === 'gla' && b !== 'gla') {
    return -1;
  }
  if (b === 'gla' && a !== 'gla') {
    return 1;
  }
  return TIMED_CONTENT_LABEL[a].localeCompare(TIMED_CONTENT_LABEL[b]);
}

type ProgressMonitorTabKind = 'passage' | 'word-list';

/**
 * Passage wins when `readingPassage` is non-blank, even if `words` is also set. Contents with
 * neither a passage nor a word list are skipped so they never become a tab.
 */
function progressMonitorTabKind(assessment: CoachPortalInAppAssessment): ProgressMonitorTabKind | null {
  if (assessment.readingPassage?.trim()) {
    return 'passage';
  }
  if (assessment.words.length > 0) {
    return 'word-list';
  }
  return null;
}

function progressMonitorTabLabel(kind: ProgressMonitorTabKind): string {
  return kind === 'passage' ? 'Passage' : 'Word list';
}

function orderedRunnablePmAssessments(assessments: CoachPortalInAppAssessment[]): CoachPortalInAppAssessment[] {
  const runnable = assessments.filter((assessment) => progressMonitorTabKind(assessment) != null);
  return [
    ...runnable.filter((assessment) => progressMonitorTabKind(assessment) === 'passage'),
    ...runnable.filter((assessment) => progressMonitorTabKind(assessment) === 'word-list'),
  ];
}

function tokensForAssessment(assessment: CoachPortalInAppAssessment | undefined): string[] {
  if (!assessment) {
    return [];
  }
  const passage = assessment.readingPassage?.trim();
  if (passage) {
    return tokenizePassage(passage);
  }
  return assessment.words;
}

function firstAssessmentResultId(students: InAppAssessmentStudent[]): string | null {
  return students[0]?.assessments[0]?.assessmentResultId ?? null;
}

function studentForResultId(
  students: InAppAssessmentStudent[],
  resultId: string | null,
): InAppAssessmentStudent | undefined {
  if (!resultId) {
    return undefined;
  }
  return students.find((entry) => entry.assessments.some((assessment) => assessment.assessmentResultId === resultId));
}

function studentsForKind(session: CoachPortalSession, kind: TimedContentKind): InAppAssessmentStudent[] {
  const assessments = session.inAppAssessments ?? [];

  return session.students
    .flatMap((student) => {
      const matching = assessments.filter(
        (row) => row.studentId === student.id && timedContentKindFromFormat(row.format) === kind,
      );
      const contents = kind === 'progress_monitor' ? orderedRunnablePmAssessments(matching) : matching;
      if (contents.length === 0) {
        return [];
      }
      return [
        {
          student,
          displayName: student.coachFacingDisplayName ?? '',
          gradeLabel: student.grade ? `${studentYearOption(student.grade)} grade` : null,
          assessments: contents,
        },
      ];
    })
    .toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

function timedContentGroups(session: CoachPortalSession): TimedContentGroup[] {
  const present = new Set<TimedContentKind>();
  for (const assessment of session.inAppAssessments ?? []) {
    const kind = timedContentKindFromFormat(assessment.format);
    if (kind) {
      present.add(kind);
    }
  }

  return [...present]
    .map((kind) => ({
      kind,
      label: TIMED_CONTENT_LABEL[kind],
      requiresWaitingRoom: kind === 'gla',
      students: studentsForKind(session, kind),
    }))
    .filter((group) => group.students.length > 0)
    .toSorted((a, b) => compareTimedContentKinds(a.kind, b.kind));
}

function InAppAssessmentWindowBody({
  groups,
  needsPicker,
  selectedKind,
  students,
  selectedAssessmentResultId,
  runner,
  navigationLocked,
  timeUpClickMode,
  onTimeUpClickModeChange,
  onSubmitAssessment,
  onSelectKind,
  onSelectStudent,
  onSelectPmTab,
  onChangeContent,
}: {
  groups: TimedContentGroup[];
  needsPicker: boolean;
  selectedKind: TimedContentKind | null;
  students: InAppAssessmentStudent[];
  selectedAssessmentResultId: string | null;
  runner: ReturnType<typeof useInAppAssessmentRunner>;
  navigationLocked: boolean;
  timeUpClickMode: TimeUpClickMode;
  onTimeUpClickModeChange: (mode: TimeUpClickMode) => void;
  onSubmitAssessment: (options: { finishedEarly: boolean }) => void;
  onSelectKind: (kind: TimedContentKind) => void;
  onSelectStudent: (studentId: string) => void;
  onSelectPmTab: (assessmentResultId: string) => void;
  onChangeContent: () => void;
}) {
  if (groups.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground">No assessments are assigned to the students in this class.</p>
    );
  }

  if (selectedKind == null) {
    return <InAppTimedContentPicker groups={groups} onSelect={onSelectKind} />;
  }

  const selectedGroup = groups.find((group) => group.kind === selectedKind);

  return (
    <InAppAssessmentPanel
      students={students}
      selectedKind={selectedKind}
      selectedAssessmentResultId={selectedAssessmentResultId}
      navigationLocked={navigationLocked}
      timeUpClickMode={timeUpClickMode}
      onTimeUpClickModeChange={onTimeUpClickModeChange}
      onSubmitAssessment={onSubmitAssessment}
      onSelectStudent={onSelectStudent}
      onSelectPmTab={onSelectPmTab}
      runner={runner}
      contentBar={
        needsPicker && selectedGroup
          ? {
              group: selectedGroup,
              onChangeContent,
            }
          : undefined
      }
    />
  );
}

function InAppTimedContentPicker({
  groups,
  onSelect,
}: {
  groups: TimedContentGroup[];
  onSelect: (kind: TimedContentKind) => void;
}) {
  const glaGroup = groups.find((group) => group.kind === 'gla');
  const glaCount = glaGroup?.students.length ?? 0;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4"
      data-testid="in-app-assessment-content-picker"
    >
      {glaGroup && (
        <div className="mb-4">
          <h2 className="text-base font-semibold">GLA Assessment Required</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {glaCount} {glaCount === 1 ? 'student needs' : 'students need'} to complete their GLA at the beginning of
            class. Please ensure GLA lessons are opened and displayed before class begins.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <InAppTimedContentCard key={group.kind} group={group} onSelect={() => onSelect(group.kind)} />
        ))}
      </div>
    </div>
  );
}

function InAppTimedContentCard({ group, onSelect }: { group: TimedContentGroup; onSelect: () => void }) {
  const studentCountLabel = `${group.students.length} ${group.students.length === 1 ? 'student' : 'students'}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="rounded-xl border bg-background p-4 text-left transition-colors hover:bg-accent/40"
      data-testid={`in-app-assessment-content-${group.kind}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{group.label}</span>
            {group.requiresWaitingRoom && <WaitingRoomRequiredBadge />}
          </div>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground">
          {studentCountLabel}
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {group.students.map((entry) => (
          <span
            key={entry.student.id}
            className={cn(
              'rounded-full px-2.5 py-0.5 text-sm',
              group.kind === 'gla' ? 'bg-blue-100 text-blue-800' : 'bg-muted text-foreground',
            )}
          >
            {entry.displayName}
          </span>
        ))}
      </div>
      {!group.requiresWaitingRoom && (
        <p className="mt-3 text-xs text-muted-foreground">
          No waiting room needed — students can remain in the classroom.
        </p>
      )}
    </button>
  );
}

function WaitingRoomRequiredBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      <AlertTriangle className="h-3 w-3" />
      Waiting room required
    </span>
  );
}

function InAppAssessmentContentBar({
  group,
  onChangeContent,
  changeDisabled,
}: {
  group: TimedContentGroup;
  onChangeContent: () => void;
  changeDisabled: boolean;
}) {
  return (
    <div className="flex items-center gap-2 border-b px-4 py-2">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Assessment type
      </span>
      {group.requiresWaitingRoom ? (
        <>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-3 w-3" />
            {group.label}
          </span>
          <span className="text-sm text-amber-700">· Waiting room required</span>
        </>
      ) : (
        <span className="text-sm font-semibold">{group.label}</span>
      )}
      <button
        type="button"
        onClick={onChangeContent}
        disabled={changeDisabled}
        className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        data-testid="in-app-assessment-change-content"
      >
        <ChevronLeft className="h-4 w-4" />
        Change
      </button>
    </div>
  );
}

function InAppAssessmentCloseConfirm({
  studentName,
  onStay,
  onCloseAnyway,
}: {
  studentName: string;
  onStay: () => void;
  onCloseAnyway: () => void;
}) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="in-app-assessment-close-confirm-title"
        className="w-full max-w-[22rem] rounded-2xl bg-background px-5 py-4 shadow-xl"
        data-testid="in-app-assessment-close-confirm"
      >
        <p id="in-app-assessment-close-confirm-title" className="flex items-start gap-2 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          <span>
            <span className="font-semibold">{studentName}&apos;s</span> assessment has not been submitted yet. Are you
            sure you want to close it?
          </span>
        </p>
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCloseAnyway}
            className="rounded px-2 py-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="in-app-assessment-close-confirm-button-close"
          >
            Yes, close
          </button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onStay}
            data-testid="in-app-assessment-close-confirm-button-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

type PassageClickMode = 'markErrors' | 'pickLastWord' | 'disabled';

function clickModeForPhase(phase: AssessmentPhase, timeUpClickMode: TimeUpClickMode): PassageClickMode {
  switch (phase) {
    case 'running':
    case 'paused':
    case 'confirmingEarlyFinish':
    case 'earlyFinishReady':
      return 'markErrors';
    case 'timeUp':
      return timeUpClickMode;
    case 'idle':
    case 'submitting':
    case 'done':
      return 'disabled';
  }
}

type InAppAssessmentRunner = ReturnType<typeof useInAppAssessmentRunner>;

function InAppAssessmentPanel({
  students,
  selectedKind,
  selectedAssessmentResultId,
  navigationLocked,
  timeUpClickMode,
  onTimeUpClickModeChange,
  onSelectStudent,
  onSelectPmTab,
  onSubmitAssessment,
  runner,
  contentBar,
}: {
  students: InAppAssessmentStudent[];
  selectedKind: TimedContentKind;
  selectedAssessmentResultId: string | null;
  navigationLocked: boolean;
  timeUpClickMode: TimeUpClickMode;
  onTimeUpClickModeChange: (mode: TimeUpClickMode) => void;
  onSelectStudent: (studentId: string) => void;
  onSelectPmTab: (assessmentResultId: string) => void;
  onSubmitAssessment: (options: { finishedEarly: boolean }) => void;
  runner: InAppAssessmentRunner;
  contentBar?: { group: TimedContentGroup; onChangeContent: () => void };
}) {
  const {
    selectedState,
    stateByResultId,
    start,
    pause,
    reset,
    requestEarlyFinish,
    cancelEarlyFinish,
    confirmEarlyFinish,
    toggleMarkedWord,
    selectLastWord,
  } = runner;

  const selectedStudent = studentForResultId(students, selectedAssessmentResultId);
  const selectedAssessment = selectedStudent?.assessments.find(
    (assessment) => assessment.assessmentResultId === selectedAssessmentResultId,
  );
  const words = tokensForAssessment(selectedAssessment);
  const showPmTabs = selectedKind === 'progress_monitor' && (selectedStudent?.assessments.length ?? 0) > 1;

  const { phase, markedWordIndexes, lastWordIndex, secondsRemaining, submittedStats } = selectedState;

  const submitting = phase === 'submitting';
  const lastWord = lastWordIndex === null ? null : words[lastWordIndex] ?? null;
  const clickMode = clickModeForPhase(phase, timeUpClickMode);
  const finishedEarlyReady = phase === 'earlyFinishReady';
  const showSubmit = phase === 'timeUp' || finishedEarlyReady || submitting;
  const submitDisabled = submitting || (phase === 'timeUp' && lastWordIndex === null);
  const incorrectWordsCount =
    lastWordIndex === null
      ? markedWordIndexes.length
      : markedWordIndexes.filter((index) => index <= lastWordIndex).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {contentBar && (
        <InAppAssessmentContentBar
          group={contentBar.group}
          onChangeContent={contentBar.onChangeContent}
          changeDisabled={navigationLocked}
        />
      )}
      <div className="px-4 pt-3">
        <InAppAssessmentStudentSelect
          students={students}
          selectedAssessmentResultId={selectedAssessmentResultId}
          disabled={navigationLocked}
          isDone={(entry) =>
            entry.assessments.every(
              (assessment) =>
                stateByResultId[assessment.assessmentResultId]?.submittedStats != null ||
                assessment.assessmentFinished === true,
            )
          }
          onSelect={onSelectStudent}
        />
      </div>

      {showPmTabs && selectedStudent && (
        <ProgressMonitorTabs
          assessments={selectedStudent.assessments}
          selectedAssessmentResultId={selectedAssessmentResultId}
          disabled={navigationLocked}
          isFinished={(assessment) =>
            assessment.assessmentFinished === true ||
            stateByResultId[assessment.assessmentResultId]?.submittedStats != null
          }
          onSelect={onSelectPmTab}
        />
      )}

      {phase === 'done' && submittedStats && (
        <InAppAssessmentCompleteBanner
          stats={submittedStats}
          wordListAssessmentResultId={
            selectedAssessment && progressMonitorTabKind(selectedAssessment) === 'passage'
              ? selectedStudent?.assessments.find((assessment) => progressMonitorTabKind(assessment) === 'word-list')
                  ?.assessmentResultId
              : undefined
          }
          onGoToWordList={onSelectPmTab}
        />
      )}

      {phase !== 'done' && (
        <div className="mt-3">
          <InAppAssessmentTimerBar
            phase={phase}
            secondsRemaining={secondsRemaining}
            onStart={start}
            onPause={pause}
            onReset={reset}
            onFinishedEarly={requestEarlyFinish}
            showSubmit={showSubmit}
            submitDisabled={submitDisabled}
            submitting={submitting}
            onSubmit={() => onSubmitAssessment({ finishedEarly: finishedEarlyReady })}
          />
        </div>
      )}

      {phase === 'idle' && <InAppAssessmentHint />}
      {phase === 'confirmingEarlyFinish' && (
        <InAppAssessmentEarlyFinishConfirmBanner onConfirm={confirmEarlyFinish} onCancel={cancelEarlyFinish} />
      )}
      {phase === 'timeUp' && (
        <InAppAssessmentTimeUpClickMode mode={timeUpClickMode} onChange={onTimeUpClickModeChange} />
      )}

      <InAppAssessmentPassage
        words={words}
        markedWordIndexes={markedWordIndexes}
        lastWordIndex={lastWordIndex}
        clickMode={clickMode}
        onWordClick={(wordIndex) =>
          clickMode === 'pickLastWord' ? selectLastWord(wordIndex) : toggleMarkedWord(wordIndex)
        }
      />

      <InAppAssessmentFooter
        incorrectWordsCount={incorrectWordsCount}
        lastWord={lastWord}
        finishedEarly={
          finishedEarlyReady ||
          (submitting && lastWordIndex === null) ||
          (phase === 'done' && submittedStats?.finishedEarly === true)
        }
      />
    </div>
  );
}

function InAppAssessmentHint() {
  return (
    <p className="flex items-center gap-1.5 px-4 pt-2 text-xs text-muted-foreground">
      <Lightbulb className="h-3.5 w-3.5 shrink-0" aria-hidden />
      Start the timer, then click any word to mark errors.
    </p>
  );
}

function InAppAssessmentEarlyFinishConfirmBanner({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-amber-900">Did the student finish the entire passage?</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onConfirm}
              className="bg-green-700 text-white hover:bg-green-800"
              data-testid="in-app-assessment-confirm-early-finish"
            >
              Yes
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InAppAssessmentCompleteBanner({
  stats,
  wordListAssessmentResultId,
  onGoToWordList,
}: {
  stats: InAppAssessmentStats;
  wordListAssessmentResultId: string | undefined;
  onGoToWordList: (assessmentResultId: string) => void;
}) {
  const wcpm = Math.round(calculateReadingWPM(stats) ?? 0);

  return (
    <div
      className="mx-4 mt-3 rounded-xl border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800"
      data-testid="in-app-assessment-complete-banner"
    >
      <p className="font-semibold underline decoration-green-700 underline-offset-4">
        {stats.finishedEarly ? 'Completed early 🎉' : 'Assessment complete'}
      </p>
      <p className="mt-1 flex flex-wrap gap-x-4">
        <span>
          Time: <strong>{stats.totalSecondsSpent}s</strong>
        </span>
        <span>
          Words: <strong>{stats.totalWordsRead}</strong>
        </span>
        <span>
          Errors: <strong>{stats.incorrectWordsCount}</strong>
        </span>
        <span>
          WCPM: <strong>{wcpm}</strong>
        </span>
      </p>
      {wordListAssessmentResultId && (
        <div className="mt-3 flex justify-end border-t border-green-200 pt-3">
          <Button
            size="sm"
            className="bg-green-700 text-white hover:bg-green-800"
            onClick={() => onGoToWordList(wordListAssessmentResultId)}
            data-testid="in-app-assessment-go-to-word-list"
          >
            Go now →
          </Button>
        </div>
      )}
    </div>
  );
}

function InAppAssessmentTimerBar({
  phase,
  secondsRemaining,
  onStart,
  onPause,
  onReset,
  onFinishedEarly,
  showSubmit,
  submitDisabled,
  submitting,
  onSubmit,
}: {
  phase: AssessmentPhase;
  secondsRemaining: number;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  onFinishedEarly: () => void;
  showSubmit: boolean;
  submitDisabled: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const running = phase === 'running';
  const canStart = phase === 'idle' || phase === 'paused';
  const canFinishEarly = running;
  const timeExpired = phase === 'timeUp' || secondsRemaining <= 0;

  return (
    <div className="flex items-center gap-3 border-y bg-muted/40 px-4 py-3">
      {running ? (
        <Button
          variant="ghost"
          size="sm"
          icon={Pause}
          onClick={onPause}
          className="text-muted-foreground"
          data-testid="in-app-assessment-pause"
        >
          Pause
        </Button>
      ) : (
        <Button
          variant="destructive"
          size="sm"
          icon={Play}
          onClick={onStart}
          disabled={!canStart}
          data-testid="in-app-assessment-start"
        >
          Start
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={onReset}
        aria-label="Reset timer"
        className="h-8 w-8 text-muted-foreground"
        data-testid="in-app-assessment-reset"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>

      <span
        className={cn('flex-1 text-center font-mono text-3xl font-bold tabular-nums', {
          'text-muted-foreground': phase === 'done',
          'text-red-600': timeExpired && phase !== 'done',
        })}
        data-testid="in-app-assessment-countdown"
        aria-live="off"
      >
        {formatCountdown(secondsRemaining)}
      </span>

      {showSubmit ? (
        <Button
          variant="destructive"
          size="sm"
          onClick={onSubmit}
          disabled={submitDisabled}
          loading={submitting}
          data-testid="in-app-assessment-submit"
        >
          Submit assessment
        </Button>
      ) : (
        <Button
          size="sm"
          icon={Flag}
          onClick={onFinishedEarly}
          disabled={!canFinishEarly}
          className="bg-green-600 text-white hover:bg-green-700 disabled:bg-muted disabled:text-muted-foreground"
          data-testid="in-app-assessment-finished-early"
        >
          Finished early
        </Button>
      )}
    </div>
  );
}

function InAppAssessmentTimeUpClickMode({
  mode,
  onChange,
}: {
  mode: TimeUpClickMode;
  onChange: (mode: TimeUpClickMode) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-2 text-xs" role="radiogroup" aria-label="Clicking words">
      <span className="text-muted-foreground">Clicking words:</span>
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'markErrors'}
        onClick={() => onChange('markErrors')}
        className={cn(
          'rounded-md px-2.5 py-1 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          mode === 'markErrors' ? 'bg-air-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground',
        )}
        data-testid="in-app-assessment-click-mode-mark-errors"
      >
        Mark errors
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'pickLastWord'}
        onClick={() => onChange('pickLastWord')}
        className={cn(
          'rounded-md px-2.5 py-1 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          mode === 'pickLastWord'
            ? 'bg-air-primary text-white'
            : 'bg-muted text-muted-foreground hover:text-foreground',
        )}
        data-testid="in-app-assessment-click-mode-select-last-word"
      >
        Select last word
      </button>
    </div>
  );
}

/**
 * What a click on a word means, which depends on where the read is:
 * - `markErrors` while the student is reading, or after time is up in that mode
 * - `pickLastWord` after time is up, so the app can count words read through that token
 * - `disabled` before the timer starts and after submitting
 */
function InAppAssessmentPassage({
  words,
  markedWordIndexes,
  lastWordIndex,
  clickMode,
  onWordClick,
}: {
  words: string[];
  markedWordIndexes: number[];
  lastWordIndex: number | null;
  clickMode: PassageClickMode;
  onWordClick: (wordIndex: number) => void;
}) {
  const marked = new Set(markedWordIndexes);
  const interactive = clickMode !== 'disabled';

  return (
    <div
      className="flex-1 overflow-y-auto px-4 py-3 text-lg leading-9"
      data-testid="in-app-assessment-passage"
      aria-label={clickMode === 'pickLastWord' ? 'Click the last word that was read' : 'Click a word to mark an error'}
    >
      {words.map((word, index) => {
        const isMarked = marked.has(index);
        const isLastWord = lastWordIndex === index;
        const canClick =
          interactive &&
          (clickMode === 'pickLastWord'
            ? canSelectInAppAssessmentLastWord(index, markedWordIndexes)
            : isMarked || canMarkInAppAssessmentError(index, lastWordIndex));

        return (
          <button
            key={`${index}-${word}`}
            type="button"
            disabled={!canClick}
            onClick={() => onWordClick(index)}
            aria-pressed={clickMode === 'pickLastWord' ? isLastWord : isMarked}
            className={cn(
              'mr-[0.3em] rounded px-0.5 align-baseline transition-colors',
              canClick ? 'cursor-pointer hover:bg-accent' : 'cursor-default',
              !canClick && interactive && 'opacity-50',
              isMarked && !isLastWord && 'text-red-600 underline decoration-red-500 decoration-2 underline-offset-4',
              isLastWord && 'bg-neutral-900 text-white hover:bg-neutral-900',
            )}
            data-testid={`in-app-assessment-word-${index}`}
          >
            {word}
          </button>
        );
      })}
    </div>
  );
}

function InAppAssessmentFooter({
  incorrectWordsCount,
  lastWord,
  finishedEarly,
}: {
  incorrectWordsCount: number;
  lastWord: string | null;
  finishedEarly: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border-t px-4 py-3 text-sm">
      <span className="flex items-center gap-1.5">
        Wrong:
        <span
          className="rounded-md bg-red-50 px-2 py-0.5 font-semibold text-red-600"
          data-testid="in-app-assessment-wrong-count"
        >
          {incorrectWordsCount}
        </span>
      </span>
      {finishedEarly ? (
        <span
          className="rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800"
          data-testid="in-app-assessment-last-word"
        >
          Full passage
        </span>
      ) : lastWord ? (
        <span className="flex items-center gap-1.5">
          Finish:
          <span
            className="rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800"
            data-testid="in-app-assessment-last-word"
          >
            {lastWord}
          </span>
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          Finish:
          <span
            className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground"
            data-testid="in-app-assessment-last-word"
          >
            —
          </span>
        </span>
      )}
    </div>
  );
}

const AVATAR_PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-cyan-100 text-cyan-700',
];

function avatarClassName(studentId: string): string {
  const seed = [...studentId].reduce((total, character) => total + character.charCodeAt(0), 0);
  return AVATAR_PALETTE[seed % AVATAR_PALETTE.length];
}

function StudentAvatar({ studentId, displayName }: { studentId: string; displayName: string }) {
  return (
    <span
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
        avatarClassName(studentId),
      )}
      aria-hidden
    >
      {displayName.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}

function StatusBadge({ done }: { done: boolean }) {
  return done ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
      <CheckCircle2 className="h-3.5 w-3.5" />
      Done
    </span>
  ) : (
    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Pending</span>
  );
}

function StudentRow({
  entry,
  done,
  showStatus,
}: {
  entry: InAppAssessmentStudent;
  done: boolean;
  showStatus: boolean;
}) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <StudentAvatar studentId={entry.student.id} displayName={entry.displayName} />
      <span className="truncate font-semibold capitalize">{entry.displayName}</span>
      {entry.gradeLabel && (
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {entry.gradeLabel}
        </span>
      )}
      {showStatus && done && <StatusBadge done />}
    </span>
  );
}

function ProgressMonitorTabs({
  assessments,
  selectedAssessmentResultId,
  disabled,
  isFinished,
  onSelect,
}: {
  assessments: CoachPortalInAppAssessment[];
  selectedAssessmentResultId: string | null;
  disabled: boolean;
  isFinished: (assessment: CoachPortalInAppAssessment) => boolean;
  onSelect: (assessmentResultId: string) => void;
}) {
  return (
    <div className="mt-2 flex justify-center gap-6 border-b px-4" role="tablist" aria-label="Progress monitor contents">
      {assessments.map((assessment) => {
        const kind = progressMonitorTabKind(assessment);
        if (!kind) {
          return null;
        }
        const selected = assessment.assessmentResultId === selectedAssessmentResultId;
        const finished = isFinished(assessment);
        return (
          <button
            key={assessment.assessmentResultId}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            className={cn(
              '-mb-px inline-flex items-center gap-1 border-b-2 px-0 py-2 text-sm',
              selected
                ? 'border-foreground font-semibold text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              disabled && 'pointer-events-none opacity-50',
            )}
            onClick={() => onSelect(assessment.assessmentResultId)}
            data-testid={`in-app-assessment-pm-tab-${assessment.assessmentResultId}`}
          >
            {progressMonitorTabLabel(kind)}
            {finished && (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" aria-hidden />
                <span className="sr-only">completed</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function InAppAssessmentStudentSelect({
  students,
  selectedAssessmentResultId,
  disabled,
  isDone,
  onSelect,
}: {
  students: InAppAssessmentStudent[];
  selectedAssessmentResultId: string | null;
  disabled: boolean;
  isDone: (entry: InAppAssessmentStudent) => boolean;
  onSelect: (studentId: string) => void;
}) {
  const { portalContainer } = useAssessmentWindow();
  const selected = studentForResultId(students, selectedAssessmentResultId);

  return (
    <SelectPrimitive.Root value={selected?.student.id} onValueChange={onSelect} disabled={disabled}>
      <SelectPrimitive.Trigger
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring data-[state=open]:border-primary disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="in-app-assessment-student-select"
        aria-label="Select student"
      >
        {selected ? (
          <StudentRow entry={selected} done={isDone(selected)} showStatus />
        ) : (
          <span className="text-muted-foreground">Select student</span>
        )}
        <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
          <span className="text-sm">{students.length}</span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4" />
          </SelectPrimitive.Icon>
        </span>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal container={portalContainer}>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-80 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg"
        >
          <SelectPrimitive.Viewport>
            <SelectPrimitive.Group>
              <SelectPrimitive.Label className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Select student
              </SelectPrimitive.Label>
              {students.map((entry) => (
                <SelectPrimitive.Item
                  key={entry.student.id}
                  value={entry.student.id}
                  className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm outline-none data-[state=checked]:bg-accent/60 hover:bg-accent"
                  data-testid={`in-app-assessment-student-option-${entry.student.id}`}
                >
                  <SelectPrimitive.ItemText asChild>
                    <StudentRow entry={entry} done={isDone(entry)} showStatus={false} />
                  </SelectPrimitive.ItemText>
                  <StatusBadge done={isDone(entry)} />
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Group>
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
